import siteModel from '../../../../models/site/site.model';
import userMapModel from '../../../../models/user/user-map.model';
import userModel from '../../../../models/user/user.model';
import ViewBase from '../../../view-base.view';
import UserCreateCountryMapFormView from '../create/user-create-country-map-form.view';


/**
 * User Edit Map View
 * 
 * This component is used to grab all the user related country map information to pass
 * down to related components and submission of edited values to the API.
 * 
 * @author Darrus Goh
 */
export default function UserEditMapView() {
    ViewBase.call(this);

    const COUNTRY_MAP_TYPES = {
        ADMIN_YN: 'admin',
        REQUIRED_APPROVAL: 'requiredApproval',
        BU: 'BU',
        GSCMDIV: 'gscmDivisions',
        RETAILDIV: 'retailDivisions',
        USERROLE: 'userRole',
        REGION: 'managedLocations',
        SITEGROUP: 'managedSiteGroups',
        SITE: 'managedSites'
    };

    const DEFAULT_COUNTRY_DATA_MAP = {
        [COUNTRY_MAP_TYPES.ADMIN_YN]: false,
        [COUNTRY_MAP_TYPES.REQUIRED_APPROVAL]: false,
        [COUNTRY_MAP_TYPES.USERROLE]: null,
        [COUNTRY_MAP_TYPES.BU]: [],
        [COUNTRY_MAP_TYPES.GSCMDIV]: [],
        [COUNTRY_MAP_TYPES.RETAILDIV]: [],
        [COUNTRY_MAP_TYPES.REGION]: [],
        [COUNTRY_MAP_TYPES.SITEGROUP]: [],
        [COUNTRY_MAP_TYPES.SITE]: []
    }

    const DEFAULT_USER_DETAILS = {
        userId: '',
        defaultCountryCode: '',
        countryMap: {}
    };

    let userDetails = null;

    const onChange = (user) => {
        userDetails = user;
    };

    const getUserDetails = (encryptedUserId) => {
        return userModel.getUserDetailsV2(encryptedUserId)
            .then((result) => {
                if (result?.data) {
                    userDetails = structuredClone(DEFAULT_USER_DETAILS);
                    userDetails.userId = result.data.userID;
                    userDetails.defaultCountryCode = result.data.countryCode;
                }
            })
            .catch((error) => {
                this.handleError(error);
            });
    };

    const getUserMap = () => {
        return userMapModel.getByUserV2('all', 'all', userDetails.userId)
            .then((result) => {
                const userCountryMap = {};
                result.forEach((data) => {
                    // Some accounts have empty strings in countryCode or mapType.
                    // These are legacy MCS2.0 accounts, as such we skip these.
                    if (data.countryCode.trim() === '' || data.mapType.trim() === '') {
                        return;
                    }

                    if (!userCountryMap.hasOwnProperty(data.countryCode)) {
                        userCountryMap[data.countryCode] = structuredClone(DEFAULT_COUNTRY_DATA_MAP);
                    }
                    const countryDataMap = userCountryMap[data.countryCode];
                    const mapType = COUNTRY_MAP_TYPES[data.mapType];

                    switch (mapType) {
                        case COUNTRY_MAP_TYPES.BU:
                        case COUNTRY_MAP_TYPES.GSCMDIV:
                        case COUNTRY_MAP_TYPES.RETAILDIV:
                        case COUNTRY_MAP_TYPES.REGION:
                            countryDataMap[mapType].push(data.mapData);
                            break;
                        case COUNTRY_MAP_TYPES.SITEGROUP:
                            countryDataMap[mapType].push({ code: data.mapData, name: null });
                            break;
                        case COUNTRY_MAP_TYPES.USERROLE:
                            countryDataMap[mapType] = data.mapData;
                            break;
                        case COUNTRY_MAP_TYPES.ADMIN_YN:
                        case COUNTRY_MAP_TYPES.REQUIRED_APPROVAL:
                            countryDataMap[mapType] = data.mapData === 'Y';
                            break;
                    }
                });

                // Ensure that countryMap contains default country
                if (!userCountryMap.hasOwnProperty(userDetails.defaultCountryCode)) {
                    userCountryMap[userDetails.defaultCountryCode] = {};
                }

                userDetails.countryMap = userCountryMap;
            })
            .catch((error) => {
                this.handleError(error);
            });
    };

    const getAllUserManagedSites = () => {
        const promiseArray = [];
        Object.keys(userDetails.countryMap).forEach(countryCode => {
            promiseArray.push(siteModel.getManagedSiteByCountryUser(countryCode, userDetails.userId)
                .then(result => {
                    const obj = { countryCode, sites: [] };

                    if (result) {
                        obj.sites = obj.sites.concat(result.map(site => { return { code: site.siteCode, name: site.siteName } }));
                    }

                    return obj;
                }));
        });

        return Promise.allSettled(promiseArray)
            .then((results) => {
                results.forEach(result => {
                    if (result.status === 'fulfilled' && result.value.sites.length > 0) {
                        userDetails.countryMap[result.value.countryCode][COUNTRY_MAP_TYPES.SITE] = result.value.sites;
                    }
                })
            })
            .catch((error) => {
                this.handleError(error);
            });
    };

    const submit = (onClose) => (e) => {
        if (!this.validateForm(e)) {
            document.querySelector('input:invalid').scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            this.showLoading();
            userModel.editMapV2(getAPIParams(), userDetails.userId)
                .then((result) => { onClose(false, result) })
                .catch(error => { this.handleError(error) })
                .finally(() => { this.hideLoading() });
        }
    };

    const getAPIParams = () => {
        const result = [];
        for (const [countryCode, countryDetails] of Object.entries(userDetails.countryMap)) {
            const obj = {
                countryCode,
                user: userDetails.userId,
                userRole: countryDetails[COUNTRY_MAP_TYPES.USERROLE],
                adminYN: countryDetails[COUNTRY_MAP_TYPES.ADMIN_YN] ? 'Y' : 'N',
                requiredApprovalYN: countryDetails[COUNTRY_MAP_TYPES.REQUIRED_APPROVAL] ? 'Y' : 'N',
                bu: countryDetails[COUNTRY_MAP_TYPES.BU],
                managedRegion: countryDetails[COUNTRY_MAP_TYPES.REGION],
                retailDivision: countryDetails[COUNTRY_MAP_TYPES.RETAILDIV],
                gscmDiv: countryDetails[COUNTRY_MAP_TYPES.GSCMDIV],
                managedSite: countryDetails[COUNTRY_MAP_TYPES.SITE]?.map(site => site.code),
                managedSiteGroup: countryDetails[COUNTRY_MAP_TYPES.SITEGROUP]?.map(siteGroup => siteGroup.code),
            };
            result.push(obj);
        }
        return result;
    };

    return {
        oninit: ({ attrs: { encryptedUserId } }) => {
            getUserDetails(encryptedUserId)
                .then(getUserMap)
                .then(getAllUserManagedSites);
        },
        view: ({ attrs: { onClose } }) => {
            return [
                m('form.needs-validation[novalidate]', { onsubmit: submit(onClose) },
                    m('.vstack.gap-2',
                        m(UserCreateCountryMapFormView, {
                            onChange: onChange,
                            user: userDetails
                        }),
                        m(".d-grid.d-md-flex.justify-content-md-center",
                            m("button.btn.btn-primary", this.getText("LT00000005", "Submit")),
                        ),
                    )
                ),
                this.getBaseViews()];
        }
    }
}
